import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaCalendarAlt, FaUser, FaTag, FaArrowRight, FaSearch } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { getBlogs } from '../firebase/db';
import {
  normalizeLang,
  getLocalizedBlogTitle,
  getLocalizedBlogCategory,
  getLocalizedBlogExcerpt,
  getLocalizedBlogAuthor,
  getLocalizedBlogTag,
  translateBlogText
} from '../utils/blogLocalization';
import './Blogs.css';

const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const Blogs = () => {
  const { t, i18n } = useTranslation();
  const currentLang = normalizeLang(i18n.language);
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const [, setTranslateTick] = useState(0);

  useEffect(() => {
    document.title = 'Blog | Digvijay Industries';
    getBlogs().then(data => {
      setBlogs(data);
      setLoading(false);
    });
  }, []);

  // Background auto-translation for dynamic blogs that don't have _mr or _kn saved
  useEffect(() => {
    if (currentLang === 'en' || !blogs.length) return;
    let isMounted = true;
    const untranslated = blogs.filter(b => {
      if (currentLang === 'mr') return !b.title_mr && !b.titleMr;
      if (currentLang === 'kn') return !b.title_kn && !b.titleKn;
      return false;
    });

    if (untranslated.length > 0) {
      Promise.all(
        untranslated.map(async (b) => {
          if (b.title) await translateBlogText(b.title, currentLang);
          if (b.excerpt) await translateBlogText(b.excerpt, currentLang);
          if (b.category) await translateBlogText(b.category, currentLang);
        })
      ).then(() => {
        if (isMounted) setTranslateTick(prev => prev + 1);
      });
    }
    return () => { isMounted = false; };
  }, [blogs, currentLang]);

  const categories = ['All', ...new Set(blogs.map(b => b.category).filter(Boolean))];

  const filtered = blogs.filter(b => {
    const s = search.trim().toLowerCase();
    const title = getLocalizedBlogTitle(b, currentLang).toLowerCase();
    const origTitle = (b.title || '').toLowerCase();
    const excerpt = getLocalizedBlogExcerpt(b, currentLang).toLowerCase();
    const origExcerpt = (b.excerpt || '').toLowerCase();

    const matchSearch = s === '' || 
      title.includes(s) || origTitle.includes(s) ||
      excerpt.includes(s) || origExcerpt.includes(s) ||
      (b.tags && b.tags.some(tg => tg.toLowerCase().includes(s) || getLocalizedBlogTag(tg, currentLang).toLowerCase().includes(s)));
      
    const matchCat = activeCategory === 'All' || b.category === activeCategory;
    return matchSearch && matchCat;
  });

  return (
    <div className="blogs-page">
      {/* Hero */}
      <div className="page-header">
        <div className="banner-particle bp-1" />
        <div className="banner-particle bp-2" />
        <div className="banner-particle bp-3" />
        <div className="container">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <span className="banner-badge">{t('blogsBadge')}</span>
            <h1>{t('blogsTitle1')} <span className="highlight-red">{t('blogsTitle2')}</span></h1>
          </motion.div>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.2 }}>
            {t('blogsSubtitle')}
          </motion.p>
        </div>
      </div>

      {/* Filters */}
      <section className="blogs-filter-bar">
        <div className="container blogs-filter-inner">
          <div className="search-box">
            <FaSearch />
            <input
              type="text"
              placeholder={t('searchArticles')}
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <div className="category-pills">
            {categories.map(cat => (
              <button
                key={cat}
                className={`pill ${activeCategory === cat ? 'active' : ''}`}
                onClick={() => setActiveCategory(cat)}
              >
                {getLocalizedBlogCategory(cat, t, currentLang)}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="section">
        <div className="container">
          {loading ? (
            <div className="loading-grid">
              {[1,2,3].map(i => <div key={i} className="skeleton-card" />)}
            </div>
          ) : filtered.length === 0 ? (
            <div className="no-results">
              <h3>{t('noArticlesFound')}</h3>
              <p>{t('tryDifferentSearch')}</p>
            </div>
          ) : (
            <motion.div className="blogs-grid">
              {filtered.map((blog) => (
                <motion.article key={blog.id} className="blog-card" variants={fadeInUp} whileHover={{ y: -8 }}>
                  <div className="blog-card-image">
                    <img
                      src={blog.image || 'https://via.placeholder.com/800x450?text=Blog+Post'}
                      alt={getLocalizedBlogTitle(blog, currentLang)}
                    />
                    {blog.category && (
                      <span className="blog-category-badge">
                        {getLocalizedBlogCategory(blog.category, t, currentLang, blog)}
                      </span>
                    )}
                  </div>
                  <div className="blog-card-body">
                    <div className="blog-meta">
                      <span><FaCalendarAlt /> {blog.date || (blog.createdAt ? new Date(blog.createdAt).toLocaleDateString() : '')}</span>
                      <span><FaUser /> {getLocalizedBlogAuthor(blog.author, currentLang, blog)}</span>
                    </div>
                    <h2 className="blog-title">{getLocalizedBlogTitle(blog, currentLang)}</h2>
                    <p className="blog-excerpt">{getLocalizedBlogExcerpt(blog, currentLang)}</p>
                    {blog.tags && blog.tags.length > 0 && (
                      <div className="blog-tags">
                        <FaTag />
                        {blog.tags.slice(0, 3).map(tg => (
                          <span key={tg} className="tag">{getLocalizedBlogTag(tg, currentLang)}</span>
                        ))}
                      </div>
                    )}
                    <Link to={`/blogs/${blog.id}`} className="read-more-btn">
                      {t('readArticle')} <FaArrowRight />
                    </Link>
                  </div>
                </motion.article>
              ))}
            </motion.div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Blogs;
